import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Users, Download, Copy, Printer, Columns, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { config } from '@/core/config'
import { useAuthStore } from '@/store/auth.store'
import { empDocumentService } from '@/services/empDocumentService'
import type { EmpDocumentListItem } from '@/api/types'
import { useEmployeeListScope } from '@/hooks/useEmployeeListScope'
import { EmployeeListScopeDialog } from '@/components/employee-scope/EmployeeListScopeDialog'

/** Relative paths from API need the API origin (same as items / employee attachments). */
function resolveDocumentAttachUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

export const EmpDocumentsList: React.FC = () => {
  const { t } = useTranslation('common')
  const te = (key: string) => t(`empDocuments.${key}`)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const { empId, pickerOpen, confirmEmployee, openEmployeePicker, handlePickerOpenChange } = useEmployeeListScope()

  const [documents, setDocuments] = useState<EmpDocumentListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [pendingAdd, setPendingAdd] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    name: true,
    description: true,
    createdDate: true,
    document: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId || !empId) return
    const fetchDocuments = async () => {
      try {
        setLoading(true)
        const res = await empDocumentService.getList(branchId, empId, pageNumber, pageSize)
        setDocuments(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (error: any) {
        const status = error?.response?.status
        const msg = (error?.response?.data?.message || error?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setDocuments([])
        setTotalRecords(0)
        if (!isNoData) {
          toast.error(error?.response?.data?.message ?? error?.message ?? te('fetchFailed'))
        }
      } finally {
        setLoading(false)
      }
    }
    fetchDocuments()
  }, [branchId, empId, pageNumber, pageSize])

  useEffect(() => {
    if (empId) return
    setDocuments([])
    setTotalRecords(0)
    setSearchQuery('')
    setPageNumber(1)
    setPendingAdd(false)
  }, [empId])

  useEffect(() => {
    if (!pendingAdd || !empId) return
    setPendingAdd(false)
    navigate(`/emp-documents/new?empId=${empId}`)
  }, [pendingAdd, empId, navigate])

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents
    const q = searchQuery.toLowerCase()
    return documents.filter(
      (d) =>
        (d.documentName ?? '').toLowerCase().includes(q) ||
        (d.documentDiscription ?? '').toLowerCase().includes(q)
    )
  }, [documents, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const visibleColumnCount =
    [
      columnVisibility.name,
      columnVisibility.description,
      columnVisibility.createdDate,
      columnVisibility.document,
      columnVisibility.action,
    ].filter(Boolean).length || 1

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      const d = new Date(dateString)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return String(dateString).slice(0, 10)
    }
  }

  const handleAdd = () => navigate(`/emp-documents/new?empId=${empId}`)
  const handleAddClick = () => {
    if (empId) return handleAdd()
    setPendingAdd(true)
    openEmployeePicker()
  }
  const handleEdit = (id: number) => navigate(`/emp-documents/${id}/edit?empId=${empId}`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(te('deleteTitle'), te('deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empDocumentService.deleteDocument(id)
      await showSuccess(te('deletedTitle'), te('deletedMessage'))
      if (branchId && empId) {
        const res = await empDocumentService.getList(branchId, empId, pageNumber, pageSize)
        setDocuments(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError(te('deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? te('deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [
      te('columnName'),
      te('columnDescription'),
      te('columnCreatedDate'),
      te('columnDocument'),
    ]
    const rows = filteredDocuments.map((d) =>
      [
        d.documentName ?? '',
        (d.documentDiscription ?? '').replace(/\r?\n/g, ' '),
        d.createdDate ? formatDate(d.createdDate) : '',
        d.documentAttachUrl ? resolveDocumentAttachUrl(d.documentAttachUrl) : '',
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(',')
    )
    const csv = [headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = te('csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(te('csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredDocuments
      .map((d) =>
        [
          d.documentName ?? '',
          d.documentDiscription ?? '',
          d.createdDate ? formatDate(d.createdDate) : '',
          d.documentAttachUrl ? resolveDocumentAttachUrl(d.documentAttachUrl) : '',
        ].join('\t')
      )
      .join('\n')
    void navigator.clipboard.writeText(text).then(() => toast.success(te('copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={te('listTitle')} description={te('listDescriptionShort')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            {te('selectBranchFirst')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {branchId ? (
        <EmployeeListScopeDialog
          open={pickerOpen}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setPendingAdd(false)
            handlePickerOpenChange(nextOpen)
          }}
          branchId={branchId}
          onConfirm={confirmEmployee}
          currentEmpId={empId}
        />
      ) : null}
      <PageHeader
        title={te('listTitle')}
        description={
          empId
            ? `${te('listDescription')} · ${t('employeeListScope.selectedEmployee', { id: empId })}`
            : te('listDescription')
        }
        action={{ label: t('common.add'), onClick: handleAddClick, icon: Plus, disabled: !empId }}
      >
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openEmployeePicker}>
          <Users className="h-4 w-4" />
          {t('employeeListScope.changeEmployee')}
        </Button>
      </PageHeader>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-2">
          <div className="relative w-64 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              disabled={!empId}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!empId}>
                  <Columns className="w-4 h-4 mr-2" />
                  {te('columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.name}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, name: !!v }))}
                >
                  {te('columnName')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.description}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, description: !!v }))}
                >
                  {te('columnDescription')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  {te('columnCreatedDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.document}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, document: !!v }))}
                >
                  {te('columnDocument')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv} disabled={!empId}>
              <Download className="w-4 h-4 mr-2" />
              {te('csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={!empId}>
              <Copy className="w-4 h-4 mr-2" />
              {te('copy')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={!empId}>
              <Printer className="w-4 h-4 mr-2" />
              {te('print')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {columnVisibility.name ? (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {te('columnName')}
                      </TableHead>
                    ) : null}
                    {columnVisibility.description ? (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {te('columnDescription')}
                      </TableHead>
                    ) : null}
                    {columnVisibility.createdDate ? (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {te('columnCreatedDate')}
                      </TableHead>
                    ) : null}
                    {columnVisibility.document ? (
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {te('columnDocument')}
                      </TableHead>
                    ) : null}
                    {columnVisibility.action ? (
                      <TableHead className="h-12 px-4 w-[100px] text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {te('columnAction')}
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                {!empId ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-10 text-muted-foreground">
                      {t('employeeListScope.dialogDescription', {
                        defaultValue:
                          'Choose an employee to load their records. You can change this later from the list page.',
                      })}
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="p-0">
                      <ListLoader message={te('loading')} />
                    </TableCell>
                  </TableRow>
                ) : filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {te('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc) => {
                    const docHref = doc.documentAttachUrl ? resolveDocumentAttachUrl(doc.documentAttachUrl) : ''
                    return (
                      <TableRow
                        key={doc.id}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        {columnVisibility.name ? (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {doc.documentName ?? '—'}
                          </TableCell>
                        ) : null}
                        {columnVisibility.description ? (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[240px]">
                            <span className="line-clamp-2 break-words">{doc.documentDiscription ?? '—'}</span>
                          </TableCell>
                        ) : null}
                        {columnVisibility.createdDate ? (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(doc.createdDate)}
                          </TableCell>
                        ) : null}
                        {columnVisibility.document ? (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {docHref ? (
                              <div className="flex justify-center">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-primary"
                                  asChild
                                  title={te('viewLink')}
                                >
                                  <a href={docHref} target="_blank" rel="noopener noreferrer">
                                    <Eye className="h-4 w-4" />
                                    <span className="sr-only">{te('viewLink')}</span>
                                  </a>
                                </Button>
                              </div>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        ) : null}
                        {columnVisibility.action ? (
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(doc.id!)}
                                className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(doc.id!)}
                                disabled={deletingId === doc.id}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {t('empDocuments.paginationShowing', {
                from: totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                to: Math.min(pageNumber * pageSize, totalRecords),
                total: totalRecords,
              })}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
                disabled={!empId}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{te('perPage')}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!empId || pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {t('empDocuments.pageOf', { page: pageNumber, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!empId || pageNumber >= totalPages || loading || totalRecords === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
